import { TourActions } from '../constants'
import { trackEvent } from 'aria-viewer/core/middleware/analyticsMiddleware'
import * as EVENTS from 'aria-viewer/analytics/constants/mobileAnalytics'
import {
    TRIGGER_IN_SCENE,
    HOTSPOT_IN_SCENE,
    NEXT_BUTTON_CLICKED,
    INFO_ICON_CLICKED,
    USER_REGISTRATION_OPT_IN_PROMPT_SHOWN,
    USER_REGISTRATION_OPT_IN_PROMPT_BUTTON_CLICK,
} from 'aria-viewer/analytics/constants'
import { EXPERIENCELIST_EXPERIENCE_SELECTED } from 'aria-viewer/analytics/constants'
import { getTourData, getMobileDefaultProperties } from 'aria-viewer/analytics/utils/analyticUtils'
import { hasNonEnglishLangugages } from 'aria-viewer/utils/functions'
import { STOP_ANALYTICS_INITIATED_BY } from 'aria-viewer/tour/constants'
import { SET_CONTEXT_MENU, AUDIO_LANGUAGE_MENU } from 'aria-viewer/contextmenu/contextMenuActions'
import { LANGUAGE_CHANGE } from 'aria-viewer/language/languageActions'
import {
    ANALYTICS_EXP_SELECTED,
    ANALYTICS_SET_STOP_INITIATED_BY,
    ANALYTICS_INFO_BUTTON_CLICKED,
} from 'aria-viewer/analytics/analyticActions'
import { mainMedia } from '../../utils/functions'
import idx from 'idx'

const checkTourLoaded = (getState, action) => {
    const hasTourLoaded = action.value.tour === false
    if (hasTourLoaded) {
        const state = getState()

        let properties = {
            ...getMobileDefaultProperties()(getState),
            Other_Lang_Included: hasNonEnglishLangugages(state),
        }

        return trackEvent(EVENTS.M_MOBEXP_LOADED, properties)
    }
}

const tourAnalytics = {
    // Determines when the registration form was successfully filled.
    [TourActions.SET_REGISTRATION_ENABLED]: (getState, action) => {
        const { enabled } = action

        let properties = getMobileDefaultProperties()(getState)
        const { stoptitle, welcomePrompt } = getTourData()(getState)

        if (!enabled) {
            const currentModal = getState()
                .getIn(['modal', 'modalStack'])
                .get(0)

            if (currentModal == undefined) return

            const { prompt } = currentModal.content
            const event = prompt
                ? EVENTS.M_SKIPSTOPREG_SUCCESS_VIEWED
                : welcomePrompt
                ? EVENTS.M_WELREG_SUCCESS_VIEWED
                : EVENTS.M_DIRREG_SUCCESS_VIEWED

            return [
                trackEvent(event, {
                    ...properties,
                    Stop: stoptitle,
                }),
            ]
        } else {
            return [
                trackEvent(EVENTS.M_MOBEXP_REG_AVAILABLE, {
                    ...properties,
                    Stop: stoptitle,
                }),
            ]
        }
    },

    // Mobile experience loaded.
    [TourActions.LOADING]: (getState, action) => {
        if (action.value.tour != undefined) return checkTourLoaded(getState, action)
    },

    // Current stop has changed
    [TourActions.STOP_CHANGED]: (getState, action) => {
        const state = getState()
        let properties = getMobileDefaultProperties()(getState)
        const {
            stoptitle,
            stopIndex,
            type,
            totalStops,
            stopVisitedCount,
            inst_name,
            owner_id,
            loc_name,
            loc_id,
            stopid,
        } = getTourData()(getState)

        const initiatedBy = state.getIn(['analytics', 'stopInitiatedBy']) || STOP_ANALYTICS_INITIATED_BY.first_stop
        const hotspots = state.getIn(['interactiveElements', 'hotspots'])
        const triggers = state.getIn(['interactiveElements', 'triggers'])
        const id = idx(mainMedia(state), _ => _.id) || state.getIn(['tour', 'stops', 'list']).get(0).main_media_id
        const hotspot = hotspots[id]
        const trigger = triggers[id]

        let events = []

        if (stopVisitedCount <= EVENTS.M_MOBEXP_STOP_LOADED_MAX_EVENTS_SENT) {
            events.push(
                trackEvent(EVENTS.M_MOBEXP_STOP_LOADED, {
                    ...properties,
                    Stop: stoptitle,
                    Stop_Order: stopIndex,
                    Initiated_by: initiatedBy,
                    Exp_Type: type,
                    Total_Stops: totalStops,
                })
            )
        }

        if (hotspot && hotspots.length > 0) {
            events.push(
                trackEvent(HOTSPOT_IN_SCENE, {
                    Institution: inst_name,
                    Inst_ID: owner_id,
                    Location: loc_name,
                    Loc_ID: loc_id,
                    Stop: stoptitle,
                    Stop_ID: stopid,
                })
            )
        }

        if (trigger && trigger.length > 0) {
            events.push(
                trackEvent(TRIGGER_IN_SCENE, {
                    Institution: inst_name,
                    Inst_ID: owner_id,
                    Location: loc_name,
                    Loc_ID: loc_id,
                    Stop: stoptitle,
                    Stop_ID: stopid,
                })
            )
        }

        if (events.length > 0) return events
    },

    [ANALYTICS_SET_STOP_INITIATED_BY]: (getState, action) => {
        if (action.initiatedBy == STOP_ANALYTICS_INITIATED_BY.next_button) {
            const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)

            return trackEvent(NEXT_BUTTON_CLICKED, {
                Institution: inst_name,
                Inst_ID: owner_id,
                Location: loc_name,
                Loc_ID: loc_id,
                Stop: stoptitle,
                Stop_ID: stopid,
            })
        }
    },

    [ANALYTICS_INFO_BUTTON_CLICKED]: (getState, action) => {
        const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)

        return trackEvent(INFO_ICON_CLICKED, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Location: loc_name,
            Loc_ID: loc_id,
            Stop: stoptitle,
            Stop_ID: stopid,
        })
    },

    //Launch ADA mode
    [TourActions.LAUNCH_ADA_MODE]: (getState, action) => {
        const { inst_id, loc_id } = action.value

        return trackEvent(EVENTS.M_MOBEXP_ADA_MODE, {
            Loc_ID: loc_id,
            Inst_ID: inst_id,
        })
    },

    [SET_CONTEXT_MENU]: (getState, action) => {
        let eventToTrack

        const { inst_name, owner_id, stoptitle } = getTourData()(getState)

        const currentContextMenu = action.contextMenu.name

        switch (currentContextMenu) {
            case AUDIO_LANGUAGE_MENU:
                eventToTrack = EVENTS.M_LANGUAGE_ICON_CLICKED
                break
            default:
                eventToTrack = null
        }

        return (
            eventToTrack &&
            trackEvent(eventToTrack, {
                Institution: inst_name,
                Inst_ID: owner_id,
                Scene_Name: stoptitle,
            })
        )
    },

    [LANGUAGE_CHANGE]: (getState, action) => {
        const { inst_name, owner_id, stoptitle } = getTourData()(getState)
        const { language } = action.language

        return (
            stoptitle &&
            trackEvent(EVENTS.M_LANGUAGE_SELECTED, {
                Institution: inst_name,
                Inst_ID: owner_id,
                Scene_Name: stoptitle,
                Selected_Language: language,
            })
        )
    },

    [ANALYTICS_EXP_SELECTED]: (getState, action) => {
        const { inst_name, owner_id, loc_name, loc_id, stoptitle, stopid } = getTourData()(getState)

        return trackEvent(EXPERIENCELIST_EXPERIENCE_SELECTED, {
            Institution: inst_name,
            Inst_ID: owner_id,
            Location: loc_name,
            Loc_ID: loc_id,
            Stop: stoptitle,
            Stop_ID: stopid,
        })
    },

    [USER_REGISTRATION_OPT_IN_PROMPT_SHOWN]: (getState, action) => {
        return trackEvent(USER_REGISTRATION_OPT_IN_PROMPT_SHOWN)
    },

    [USER_REGISTRATION_OPT_IN_PROMPT_BUTTON_CLICK]: (getState, action) => {
        return trackEvent(USER_REGISTRATION_OPT_IN_PROMPT_BUTTON_CLICK, {
            Button_Clicked: action.selectedOption,
        })
    },
}

export default tourAnalytics
